import { useState } from 'react';
import { PatternFormat } from 'react-number-format';
import { ReactComponent as ClosedEye } from 'assets/icons/closedEye.svg';
import { ReactComponent as OpenedEye } from 'assets/icons/openedEye.svg';
import { clsx } from 'clsx';
import { INPUT_TYPES } from 'constants/interfaces';

import type ITextField from './interfaces/ITextField';

import 'react-international-phone/style.css';
import styles from './scss/TextField.module.scss';

function TextField({
  name, label, value, onChange, disabled,
  className, error, placeholder, icon, afterAddon,
  required, type = INPUT_TYPES.text, mask = '', maxLength,
  autoFocus, onBlur, onKeyDown,
}: ITextField) {
  const classes = clsx(styles.root, className, {
    [styles.rootError]: error,
  });

  const wrapperClasses = clsx(styles.wrapper, {
    [styles.withIcon]: icon,
  });

  const isPassword = type === INPUT_TYPES.password;

  const [isPasswordVisible, setIsPasswordVisible] = useState(isPassword);

  const onTextVisibility = () => {
    setIsPasswordVisible(
      (prevIsPasswordTypeVisible) => !prevIsPasswordTypeVisible,
    );
  };

  // PatternFormat only accepts a narrow `type` union, which INPUT_TYPES widens to `string`
  const inputType = (isPasswordVisible ? INPUT_TYPES.password : INPUT_TYPES.text) as 'password' | 'text';

  return (
    <div className={classes}>
      {label ? (
        <label htmlFor={name}>
          {required ? `${label}*` : label}
        </label>
      ) : null}
      <div className={wrapperClasses}>
        {icon ? (
          <div className={styles.icon}>
            {icon}
          </div>
        ) : null}

        {mask ? (
          <PatternFormat
            id={name}
            name={name}
            onChange={onChange}
            value={value}
            type={inputType}
            format={mask}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
          />
        ) : (
          <input
            id={name}
            name={name}
            onChange={onChange}
            value={value}
            type={inputType}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
          />
        )}

        {isPassword ? (
          <button
            className={styles.passwordIcon}
            type="button"
            onClick={onTextVisibility}
          >
            {isPasswordVisible ? <OpenedEye /> : <ClosedEye />}
          </button>
        ) : null}

        {afterAddon ? (
          <div className={styles.afterAddon}>
            {afterAddon}
          </div>
        ) : null}
      </div>
      {error ? <p className={styles.error}>{error}</p> : null}
    </div>
  );
}

export default TextField;
